import { useState, useCallback } from 'react';
import expenseService from '../services/expenseService';
import { Expense, ExpenseCreate, ExpenseUpdate, ExpenseFilter } from '../types/expense';
import { generateTempId, getNextSheetRow } from '../utils/expenseUtils';

export const useExpenses = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchExpenses = useCallback(async (filters: ExpenseFilter = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await expenseService.getExpenses(filters);
      setExpenses(response.expenses);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка загрузки расходов';
      console.warn('Ошибка загрузки расходов:', errorMessage);
      
      // Если это ошибка API (например, 429), показываем пустой список
      if (errorMessage.includes('429') || errorMessage.includes('Quota exceeded')) {
        setExpenses([]);
        setError(null); // Не показываем ошибку пользователю
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const createExpense = async (data: ExpenseCreate) => {
    // Создаем временный расход для немедленного отображения
    const tempId = generateTempId();
    const tempExpense: Expense = {
      ...data,
      expense_id: tempId,
      temp_id: tempId,
      sheet_row: getNextSheetRow(expenses),
      synced: false, // Будет помечен как "Синхронизируем..."
      restaurant_name: '', // Будет заполнено после получения данных
      expense_date: new Date().toISOString().split('T')[0], // Добавляем текущую дату
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: '' // Добавляем обязательное поле
    };

    try {
      // Добавляем временный расход в список с меткой "Синхронизируем..."
      setExpenses(prev => [tempExpense, ...prev]);

      // Отправляем на сервер
      const result = await expenseService.createExpense(data);
      
      // Определяем статус синхронизации из ответа backend
      const isSynced = result.status === 'created_and_synced';
      
      // Заменяем временный расход на реальный с актуальным статусом синхронизации
      setExpenses(prev => prev.map(exp => 
        exp.temp_id === tempId ? { 
          ...exp, 
          expense_id: result.expense_id, 
          synced: isSynced
        } : exp
      ));

      return { ...tempExpense, expense_id: result.expense_id, synced: isSynced };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка создания расхода');
      // Удаляем временный расход при ошибке
      setExpenses(prev => prev.filter(exp => exp.temp_id !== tempId));
      throw err;
    }
  };

  const syncWithGoogleSheets = async (expenseId: string) => {
    try {
      await expenseService.syncWithGoogleSheets(expenseId);
      setExpenses(prev => prev.map(exp => 
        exp.expense_id === expenseId ? { ...exp, synced: true } : exp
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка синхронизации с Google Sheets');
      throw err;
    }
  };

  const bulkSyncWithGoogleSheets = async (expenseIds: string[]) => {
    try {
      await expenseService.bulkSyncWithGoogleSheets(expenseIds);
      setExpenses(prev => prev.map(exp => 
        expenseIds.includes(exp.expense_id) ? { ...exp, synced: true } : exp
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка массовой синхронизации');
      throw err;
    }
  };

  const updateExpense = async (id: string, data: ExpenseUpdate) => {
    try {
      // Сначала отправляем запрос на сервер
      await expenseService.updateExpense(id, data);
      
      // Обновляем локальное состояние с данными от сервера
      setExpenses(prev => prev.map(exp => 
        exp.expense_id === id ? { 
          ...exp, 
          ...data, 
          synced: true, // Помечаем как синхронизированный
          updated_at: new Date().toISOString()
        } : exp
      ));
      
      return { ...data, expense_id: id, synced: true } as Expense;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка обновления расхода');
      throw err;
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    expenses,
    loading,
    error,
    fetchExpenses,
    createExpense,
    updateExpense,
    syncWithGoogleSheets,
    bulkSyncWithGoogleSheets,
    clearError
  };
};
